import { computed, inject, ref, type Component, type InjectionKey, type Ref } from 'vue';

/** What an entry is — the palette's prefix modes filter on it. */
export type NxCommandCategory = 'navigation' | 'action' | 'setting' | 'record' | 'person' | 'help';

/** Why a search result matched; exact results rise into „Beste Treffer“. */
export type NxCommandMatch = 'exact' | 'list' | 'prefix' | 'text';

/** Data the palette shows next to the list for the highlighted entry. */
export interface NxCommandPreview {
  title: string;
  subtitle?: string;
  /** Label/value rows (number, customer, date …). */
  fields?: { label: string; value: string }[];
  /** Longer plain text (a mail excerpt, a description). */
  text?: string;
}

export interface NxCommand {
  /** Stable identifier, unique across all registered commands */
  id: string;
  /** Display label */
  label: string;
  /** Optional logical group (e.g. "Forge", "Agent", "Global") */
  group?: string;
  /** Optional heading inside a search source's group (e.g. „Angebote“ under „ERP“) */
  subgroup?: string;
  /** Optional lucide icon component */
  icon?: Component;
  /** Optional muted helper line under the label */
  hint?: string;
  /** Optional keyboard hint shown on the right (e.g. "⌘N") */
  shortcut?: string;
  /**
   * Binds a key combination while the palette is closed, e.g. "mod+shift+l"
   * (`mod` = ⌘ on macOS, Ctrl elsewhere). Leave it unset when the app binds
   * the key itself and `shortcut` only documents it.
   */
  bind?: string;
  /**
   * The bound function key fires in a text field too — F9 „Stapel buchen“
   * while the cursor stands in a posting's amount. Without it a binding
   * without ⌘/Ctrl stays quiet while the user types. Honoured for F1–F12
   * only, which type nothing, so a field never loses a key it takes.
   */
  bindWhenTyping?: boolean;
  /** Optional secondary search tokens (synonyms, abbreviations) */
  keywords?: string[];
  /** What the entry is; defaults to 'action'. */
  category?: NxCommandCategory;
  /** The app the entry belongs to — ranked first while that app is open. */
  app?: string;
  /** Shown only while this returns true (a record open, rows selected …). */
  when?: () => boolean;
  /** The location the entry opens: ⌘↵ opens it in a new tab, ⇧↵ copies it. */
  href?: string;
  /** Why a search result matched (search sources only). */
  match?: NxCommandMatch;
  /** Plain text around a search hit; the palette marks the query terms. */
  snippet?: string;
  /** Picking the entry opens this list instead of running it. */
  children?: () => NxCommand[] | Promise<NxCommand[]>;
  /** Picking the entry asks for a text first, then runs with it. */
  input?: { placeholder?: string; run: (value: string) => void | Promise<void> };
  /** Further actions on the entry, offered with Tab / → (open, copy, …). */
  actions?: NxCommand[];
  /** Data for the preview next to the list. */
  preview?: NxCommandPreview | (() => Promise<NxCommandPreview | null>);
  /** Not remembered under „Zuletzt benutzt“ (e.g. „Mehr anzeigen“ links). */
  transient?: boolean;
  /** Executed when the user picks this command */
  run: () => void | Promise<void>;
}

/**
 * A keyword scope of a search source: typing one of its keywords first
 * („angebot 3333“) narrows the search to that source and scope.
 */
export interface NxCommandScope {
  /** Stable id the source understands (e.g. "erp.quote"). */
  id: string;
  /** Chip label („Angebote“). */
  label: string;
  /** Lower-case words that select the scope. */
  keywords: string[];
  icon?: Component;
}

export interface NxCommandSearchContext {
  /** The scope the query was narrowed to by a leading keyword. */
  scope?: string;
}

/** What a suggester is told besides the word it is asked about. */
export interface NxCommandSuggestContext extends NxCommandSearchContext {
  /**
   * The whole free text of the query (without mode chip and scope keyword),
   * of which the asked word is the last. A suggester may look at the other
   * words; the palette still replaces only the last one.
   */
  query?: string;
}

/**
 * An asynchronous search source: records (customers, tasks, tickets, …) the
 * palette finds while the user types, in addition to the static commands.
 * Each result is an ordinary `NxCommand` — its `run` opens the record.
 */
export interface NxCommandSource {
  /** Stable identifier, unique across all registered sources */
  id: string;
  /** Group heading for this source's results (e.g. "Aufgaben") */
  group?: string;
  /** The source is only asked from this many characters on. Defaults to 2. */
  minQueryLength?: number;
  /** Position among the sources, lower first (the account's priority). */
  order?: number;
  /** What the results are, for the `#` and `@` modes; defaults to 'record'. */
  category?: NxCommandCategory;
  /** Keyword scopes that narrow a query to this source. */
  scopes?: NxCommandScope[];
  /**
   * Results for `query`. A newer query aborts `signal`; a source that talks to
   * a server passes it on to `fetch`. Results of an aborted call are dropped
   * either way. A rejected promise shows the source as unavailable.
   */
  search(
    query: string,
    signal: AbortSignal,
    context?: NxCommandSearchContext
  ): Promise<NxCommand[]>;
}

/** One spelling a suggester offers for the word the user typed. */
export interface NxCommandSuggestion {
  /** The corrected spelling. */
  text: string;
  /** Higher wins. The palette shows only the best. */
  score: number;
  /** Where it came from (e.g. "erp.product"). The palette does not show it. */
  source?: string;
  /**
   * Default `true`. `false` takes „„X“ ist richtig“ away from the row: the
   * suggestion is no spelling of the typed word (a synonym), so the word has
   * nothing to be confirmed as.
   */
  confirmable?: boolean;
}

/**
 * An asynchronous spelling suggester. The palette asks every registered one
 * for an eligible last word and shows the single best answer. It never changes
 * the hits by itself.
 */
export interface NxCommandSuggester {
  /** Stable identifier, unique across all registered suggesters. */
  id: string;
  /**
   * Suggestions for `term` (one word). A newer query aborts `signal`. A
   * rejected promise is ignored — the palette shows no error for it.
   */
  suggest(
    term: string,
    signal: AbortSignal,
    context?: NxCommandSuggestContext
  ): Promise<NxCommandSuggestion[]>;
  /**
   * The user says `text` (the typed word) is correct. What that stores is the
   * suggester's choice; the palette only hides the row.
   */
  confirm?(text: string): void;
  /**
   * The user accepted `text` in place of the typed word `original`. Called on
   * every registered suggester after the palette has replaced that word.
   * What that stores is the suggester's choice; a suggester without `accept`
   * is left unchanged.
   */
  accept?(original: string, text: string): void;
  /**
   * The user asked to stop suggesting `text` (the suggested word) for the
   * typed word `original`. Called on the suggester whose suggestion won, only
   * when it has the hook — the palette shows the „nicht mehr vorschlagen“ link
   * only for such a suggestion. What that stores is the suggester's choice.
   * A hook that returns at once hides the row for that input. A hook that
   * returns a promise is awaited: its resolution hides the row, its rejection
   * (or a synchronous throw) keeps the row, so the user can try again. The
   * palette shows no error for it — the suggester reports a failure itself.
   */
  neverSuggest?(original: string, text: string): void | Promise<void>;
}

/**
 * The entry the palette offers when nothing matched, e.g. „Assistent fragen:
 * <Eingabe>“. It receives the raw query.
 */
export interface NxCommandFallback {
  id: string;
  label: (query: string) => string;
  icon?: Component;
  run: (query: string) => void | Promise<void>;
}

/** A remembered entry: enough to show and reopen it without its source. */
export interface NxCommandMemoryEntry {
  id: string;
  label: string;
  hint?: string;
  group?: string;
  href?: string;
  category?: NxCommandCategory;
}

export interface NxCommandMemory {
  recents: NxCommandMemoryEntry[];
  pins: NxCommandMemoryEntry[];
}

/** Where the registry keeps recents and pins (per account, across devices). */
export interface NxCommandMemoryStore {
  load(): Promise<NxCommandMemory | null>;
  save(memory: NxCommandMemory): void | Promise<void>;
}

export interface NxCommandContext {
  /** The app currently open, matched against `NxCommand.app`. */
  app?: string;
}

export interface NxCommandRegistry {
  commands: Ref<NxCommand[]>;
  sources: Ref<NxCommandSource[]>;
  suggesters: Ref<NxCommandSuggester[]>;
  fallbacks: Ref<NxCommandFallback[]>;
  isOpen: Ref<boolean>;
  /** Text the palette starts with on its next open (see `open(query)`). */
  initialQuery: Ref<string>;
  /** Counts the opens, so an open right after a close is never lost. */
  openCount: Ref<number>;
  context: Ref<NxCommandContext>;
  memory: Ref<NxCommandMemory>;
  /** Opens a remembered entry that has no live command (navigates to its href). */
  navigate: Ref<((href: string) => void) | null>;
  /** Turns an `href` into a browser URL (e.g. prefixes the hash of a hash router). */
  resolveUrl: Ref<((href: string) => string) | null>;
  /** How many recents are kept. */
  recentLimit: number;
  /** Register a batch; returns an unregister function for use in onBeforeUnmount */
  register(cmds: NxCommand[]): () => void;
  /** Register a search source; returns an unregister function */
  registerSource(source: NxCommandSource): () => void;
  /** Register a spelling suggester; returns an unregister function */
  registerSuggester(suggester: NxCommandSuggester): () => void;
  /** Register the entry offered when nothing matched; returns an unregister function */
  registerFallback(fallback: NxCommandFallback): () => void;
  /** Opens the palette, optionally with a query already typed (e.g. "?"). */
  open(query?: string): void;
  close(): void;
  toggle(): void;
  /** Records a run under „Zuletzt benutzt“. */
  remember(cmd: NxCommand): void;
  pin(cmd: NxCommand): void;
  unpin(id: string): void;
  isPinned(id: string): boolean;
  /** Loads recents and pins from a store and saves every change back. */
  useMemoryStore(store: NxCommandMemoryStore): Promise<void>;
}

export const NxCommandRegistryKey: InjectionKey<NxCommandRegistry> = Symbol('NxCommandRegistry');

function snapshot(cmd: NxCommand): NxCommandMemoryEntry {
  const entry: NxCommandMemoryEntry = { id: cmd.id, label: cmd.label };
  if (cmd.hint) entry.hint = cmd.hint;
  if (cmd.group) entry.group = cmd.group;
  if (cmd.href) entry.href = cmd.href;
  if (cmd.category) entry.category = cmd.category;
  return entry;
}

/** Keeps only well-formed entries of a stored memory document. */
export function sanitizeNxCommandMemory(raw: unknown): NxCommandMemory {
  const list = (v: unknown): NxCommandMemoryEntry[] =>
    Array.isArray(v)
      ? v.filter(
          (e): e is NxCommandMemoryEntry =>
            !!e &&
            typeof e === 'object' &&
            typeof (e as NxCommandMemoryEntry).id === 'string' &&
            typeof (e as NxCommandMemoryEntry).label === 'string'
        )
      : [];
  const m = (raw ?? {}) as Partial<NxCommandMemory>;
  return { recents: list(m.recents), pins: list(m.pins) };
}

export function createNxCommandRegistry(options: { recentLimit?: number } = {}): NxCommandRegistry {
  const commands = ref<NxCommand[]>([]);
  const sources = ref<NxCommandSource[]>([]);
  const suggesters = ref<NxCommandSuggester[]>([]);
  const fallbacks = ref<NxCommandFallback[]>([]);
  const isOpen = ref(false);
  const initialQuery = ref('');
  const openCount = ref(0);
  const context = ref<NxCommandContext>({});
  const memory = ref<NxCommandMemory>({ recents: [], pins: [] });
  const navigate = ref<((href: string) => void) | null>(null);
  const resolveUrl = ref<((href: string) => string) | null>(null);
  const recentLimit = options.recentLimit ?? 8;
  let store: NxCommandMemoryStore | null = null;

  function register(cmds: NxCommand[]): () => void {
    commands.value = [...commands.value, ...cmds];
    return () => {
      const ids = new Set(cmds.map((c) => c.id));
      commands.value = commands.value.filter((c) => !ids.has(c.id));
    };
  }

  function registerSource(source: NxCommandSource): () => void {
    sources.value = [...sources.value.filter((s) => s.id !== source.id), source];
    // By id: the ref hands back reactive proxies, never the object passed in.
    return () => {
      sources.value = sources.value.filter((s) => s.id !== source.id);
    };
  }

  function registerSuggester(suggester: NxCommandSuggester): () => void {
    suggesters.value = [...suggesters.value.filter((s) => s.id !== suggester.id), suggester];
    return () => {
      suggesters.value = suggesters.value.filter((s) => s.id !== suggester.id);
    };
  }

  function registerFallback(fallback: NxCommandFallback): () => void {
    fallbacks.value = [...fallbacks.value.filter((f) => f.id !== fallback.id), fallback];
    return () => {
      fallbacks.value = fallbacks.value.filter((f) => f.id !== fallback.id);
    };
  }

  function open(query = '') {
    initialQuery.value = query;
    openCount.value++;
    isOpen.value = true;
  }
  function close() {
    isOpen.value = false;
  }
  function toggle() {
    if (isOpen.value) close();
    else open();
  }

  function persist() {
    void store?.save(memory.value);
  }

  function remember(cmd: NxCommand) {
    if (cmd.transient) return;
    const recents = [snapshot(cmd), ...memory.value.recents.filter((e) => e.id !== cmd.id)];
    memory.value = { ...memory.value, recents: recents.slice(0, recentLimit) };
    persist();
  }

  function pin(cmd: NxCommand) {
    if (memory.value.pins.some((e) => e.id === cmd.id)) return;
    memory.value = { ...memory.value, pins: [...memory.value.pins, snapshot(cmd)] };
    persist();
  }

  function unpin(id: string) {
    memory.value = { ...memory.value, pins: memory.value.pins.filter((e) => e.id !== id) };
    persist();
  }

  const pinnedIds = computed(() => new Set(memory.value.pins.map((e) => e.id)));

  async function useMemoryStore(next: NxCommandMemoryStore) {
    store = null;
    const loaded = await next.load().catch(() => null);
    if (loaded) memory.value = sanitizeNxCommandMemory(loaded);
    store = next;
  }

  return {
    commands,
    sources,
    suggesters,
    fallbacks,
    isOpen,
    initialQuery,
    openCount,
    context,
    memory,
    navigate,
    resolveUrl,
    recentLimit,
    register,
    registerSource,
    registerSuggester,
    registerFallback,
    open,
    close,
    toggle,
    remember,
    pin,
    unpin,
    isPinned: (id) => pinnedIds.value.has(id),
    useMemoryStore,
  };
}

/**
 * Use the command registry provided by the Connect shell. Returns `null`
 * in standalone mode where the shell isn't present — call sites should
 * skip registration gracefully when this happens.
 */
export function useNxCommands(): NxCommandRegistry | null {
  return inject(NxCommandRegistryKey, null);
}
